// src/components/dialogs/ConfirmDialog.js

import React from 'react';
import PropTypes from 'prop-types';

/**
 * 確認ダイアログコンポーネント
 * 汎用的な確認ダイアログを表示
 */
const ConfirmDialog = ({
  isOpen = false,
  title = '',
  message = '',
  onConfirm = null,
  onCancel = null,
  confirmText = 'OK',
  cancelText = 'キャンセル',
  size = 'medium', // 'small', 'medium', 'large'
  theme = 'default', // 'default', 'dark', 'danger', 'warning'
  showCancelButton = true,
  closeOnOverlayClick = true
}) => {
  // 非表示の場合は何も表示しない
  if (!isOpen) return null;

  // サイズ設定
  const getSizeConfig = (size) => {
    switch (size) {
      case 'small':
        return {
          width: '300px',
          padding: '20px',
          titleSize: '16px',
          messageSize: '14px',
          buttonPadding: '8px 16px',
          buttonFontSize: '14px'
        };
      case 'large':
        return {
          width: '600px',
          padding: '40px',
          titleSize: '20px',
          messageSize: '16px',
          buttonPadding: '12px 24px',
          buttonFontSize: '16px'
        };
      case 'medium':
      default:
        return {
          width: '450px',
          padding: '30px',
          titleSize: '18px',
          messageSize: '15px',
          buttonPadding: '10px 20px',
          buttonFontSize: '15px'
        };
    }
  };

  // テーマ設定
  const getThemeConfig = (theme) => {
    switch (theme) {
      case 'dark':
        return {
          backgroundColor: '#2d3748',
          textColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.9)',
          borderColor: '#4a5568',
          confirmButtonBg: '#4299e1',
          confirmButtonHover: '#3182ce',
          cancelButtonBg: '#718096',
          cancelButtonHover: '#4a5568'
        };
      case 'danger':
        return {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          overlayColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: '#e53e3e',
          confirmButtonBg: '#e53e3e',
          confirmButtonHover: '#c53030',
          cancelButtonBg: '#e2e8f0',
          cancelButtonHover: '#cbd5e0'
        };
      case 'warning':
        return {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          overlayColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: '#ed8936',
          confirmButtonBg: '#ed8936',
          confirmButtonHover: '#dd6b20',
          cancelButtonBg: '#e2e8f0',
          cancelButtonHover: '#cbd5e0'
        };
      case 'default':
      default:
        return {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          overlayColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: '#e0e0e0',
          confirmButtonBg: '#007bff',
          confirmButtonHover: '#0056b3',
          cancelButtonBg: '#6c757d',
          cancelButtonHover: '#545b62'
        };
    }
  };

  const sizeConfig = getSizeConfig(size);
  const themeConfig = getThemeConfig(theme);

  // オーバーレイクリックハンドラ
  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget && onCancel) {
      onCancel();
    }
  };

  // Confirmハンドラ
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  // Cancelハンドラ
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // キーボードイベントハンドラ
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onCancel) {
      handleCancel();
    } else if (e.key === 'Enter' && onConfirm) {
      handleConfirm();
    }
  };

  return (
    <div
      className="confirm-dialog-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: themeConfig.overlayColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
        transition: 'opacity 0.2s ease'
      }}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className="confirm-dialog"
        style={{
          backgroundColor: themeConfig.backgroundColor,
          borderRadius: '12px',
          minWidth: sizeConfig.width,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${themeConfig.borderColor}`,
          position: 'relative',
          animation: 'fadeInScale 0.2s ease-out',
          outline: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className="confirm-dialog-header"
          style={{
            padding: `${sizeConfig.padding} ${sizeConfig.padding} 0`,
            borderBottom: title ? `1px solid ${theme === 'dark' ? '#4a5568' : '#f0f0f0'}` : 'none',
            marginBottom: title ? '20px' : '0'
          }}
        >
          {title && (
            <h3
              style={{
                margin: 0,
                fontSize: sizeConfig.titleSize,
                fontWeight: '600',
                color: themeConfig.textColor,
                paddingBottom: '15px'
              }}
            >
              {title}
            </h3>
          )}
        </div>

        {/* ボディ */}
        <div
          className="confirm-dialog-body"
          style={{
            padding: `0 ${sizeConfig.padding}`,
            marginBottom: '30px'
          }}
        >
          <div
            style={{
              fontSize: sizeConfig.messageSize,
              lineHeight: '1.5',
              color: themeConfig.textColor,
              whiteSpace: 'pre-line',
              wordBreak: 'break-word'
            }}
          >
            {message}
          </div>
        </div>

        {/* アクションボタン */}
        <div
          className="confirm-dialog-actions"
          style={{
            padding: `0 ${sizeConfig.padding} ${sizeConfig.padding}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            borderTop: `1px solid ${theme === 'dark' ? '#4a5568' : '#f0f0f0'}`,
            paddingTop: '20px'
          }}
        >
          {/* キャンセルボタン */}
          {showCancelButton && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: theme === 'danger' || theme === 'warning' 
                  ? themeConfig.cancelButtonBg 
                  : '#6c757d',
                color: theme === 'danger' || theme === 'warning' 
                  ? '#333' 
                  : '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: sizeConfig.buttonPadding,
                fontSize: sizeConfig.buttonFontSize,
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '80px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = theme === 'danger' || theme === 'warning' 
                  ? themeConfig.cancelButtonHover 
                  : '#545b62';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = theme === 'danger' || theme === 'warning' 
                  ? themeConfig.cancelButtonBg 
                  : '#6c757d';
              }}
              onFocus={(e) => {
                e.target.style.outline = '2px solid #007bff';
                e.target.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.target.style.outline = 'none';
              }}
            >
              {cancelText}
            </button>
          )}

          {/* 確認ボタン */}
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              backgroundColor: themeConfig.confirmButtonBg,
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: sizeConfig.buttonPadding,
              fontSize: sizeConfig.buttonFontSize,
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '80px',
              boxShadow: `0 2px 8px ${themeConfig.confirmButtonBg}33`
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = themeConfig.confirmButtonHover;
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = themeConfig.confirmButtonBg;
              e.target.style.transform = 'translateY(0)';
            }}
            onFocus={(e) => {
              e.target.style.outline = `2px solid ${themeConfig.confirmButtonBg}`;
              e.target.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.target.style.outline = 'none';
            }}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>

      {/* CSS アニメーション */}
      <style jsx>{`
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

// PropTypesの定義
ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  theme: PropTypes.oneOf(['default', 'dark', 'danger', 'warning']),
  showCancelButton: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool
};

export default ConfirmDialog;